import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlassCard } from "../components/GlassCard.js";
import { Icon } from "../components/Icon.js";
import { StatusPip } from "../components/StatusPip.js";
import {
  getEventCount,
  getHealth,
  listConnectors,
  type Connector,
  type EventCount,
  type HealthResponse,
} from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

export function DashboardPage() {
  const { user } = useAuth();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState(false);
  const [connectors, setConnectors] = useState<Connector[] | null>(null);
  const [events, setEvents] = useState<EventCount | null>(null);

  useEffect(() => {
    const refreshLive = () => {
      getHealth().then(setHealth).catch(() => setHealthError(true));
      getEventCount().then(setEvents).catch(() => setEvents(null));
      listConnectors()
        .then((r) => setConnectors(r.connectors))
        .catch(() => setConnectors([]));
    };
    refreshLive();
    const id = window.setInterval(refreshLive, 30_000);
    return () => window.clearInterval(id);
  }, []);

  const counts = countByStatus(connectors);

  return (
    <div className="grid grid-cols-12 gap-gutter">
      {/* Greeting + hub status */}
      <GlassCard className="col-span-12 p-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-md">
          <div>
            <h1 className="text-display-xl text-on-background">
              Welcome{user?.name ? `, ${user.name.split(/\s+/)[0]}` : ""}.
            </h1>
            <p className="text-body-base text-on-surface-variant mt-1">
              {summaryLine(connectors, counts)}
            </p>
          </div>
          <HubLink health={health} error={healthError} />
        </div>
      </GlassCard>

      {/* KPIs */}
      <Kpi
        icon="mail"
        label="Gmail accounts"
        value={connectors === null ? "…" : String(counts.googleActive)}
        sub={connectors === null ? undefined : kpiSub(counts.googlePending, counts.googleError)}
        accent="primary"
        cta={{ to: "/connectors", label: "Manage" }}
      />
      <Kpi
        icon="desktop_windows"
        label="Local Outlooks"
        value={connectors === null ? "…" : String(counts.localActive)}
        accent="secondary"
        hint={counts.localActive === 0 ? "Install the desktop app on each PC" : undefined}
      />
      <Kpi
        icon="event"
        label="Events synced"
        value={events === null ? "…" : String(events.confirmed)}
        sub={
          events && events.total > events.confirmed
            ? `${events.total - events.confirmed} cancelled`
            : undefined
        }
        accent="tertiary"
        hint={events && events.confirmed === 0 ? "First sync runs within ~60 sec of connecting" : undefined}
      />
      <Kpi
        icon="warning"
        label="Sync errors (24h)"
        value={counts.errors > 0 ? String(counts.errors) : "0"}
        accent={counts.errors > 0 ? "error" : "primary"}
      />

      {/* Main content row — different per role */}
      {user?.isAdmin ? (
        <>
          <GlassCard className="col-span-12 lg:col-span-8 p-lg">
            <h2 className="text-headline-md text-on-surface mb-md">Operator status</h2>
            <p className="text-body-sm text-on-surface-variant mb-md">
              Engineering progress on the AppsDoCalSync hub. Visible to admins only.
            </p>
            <div className="space-y-2">
              <PhaseRow done label="Hub backend" detail="Auth, sessions, Postgres + Redis, /health, audit-ready" />
              <PhaseRow done label="Web admin UI" detail="Sign-in, invite users, theme toggle, real connectors page" />
              <PhaseRow done label="Database schema" detail="users, connectors, events, change_log, invitations, device_tokens" />
              <PhaseRow done label="Recurrence translators" detail="Microsoft Graph + Google + iCalendar — 62 tests passing" />
              <PhaseRow done label="Gmail request + admin approval flow" detail="Colleagues request, you approve from /approvals" />
              <PhaseRow done label="Google OAuth scaffolding" detail="Token-encrypted at rest (AES-256-GCM)" />
              <PhaseRow inProgress label="Windows desktop app" detail=".NET 9 / Avalonia, building" />
              <PhaseRow pending label="Live event sync" detail="Activates once the desktop app is installed and the first sync runs" />
            </div>
          </GlassCard>
          <ConnectorsPanel connectors={connectors} className="col-span-12 lg:col-span-4" />
        </>
      ) : (
        <>
          <GettingStarted connectors={connectors} className="col-span-12 lg:col-span-8" />
          <ConnectorsPanel connectors={connectors} className="col-span-12 lg:col-span-4" />
        </>
      )}
    </div>
  );
}

// ---------- helpers ----------

interface Counts {
  googleActive: number;
  googlePending: number;
  googleApproved: number;
  googleRejected: number;
  googleError: number;
  localActive: number;
  errors: number;
}

function countByStatus(connectors: Connector[] | null): Counts {
  const c: Counts = {
    googleActive: 0,
    googlePending: 0,
    googleApproved: 0,
    googleRejected: 0,
    googleError: 0,
    localActive: 0,
    errors: 0,
  };
  if (!connectors) return c;
  for (const x of connectors) {
    if (x.type === "google") {
      if (x.status === "active") c.googleActive++;
      else if (x.status === "awaiting_approval") c.googlePending++;
      else if (x.status === "approved") c.googleApproved++;
      else if (x.status === "rejected") c.googleRejected++;
      else if (x.status === "error") c.googleError++;
    } else if (x.type === "outlook_pst" && x.status === "active") {
      c.localActive++;
    }
    if (x.status === "error") c.errors++;
  }
  return c;
}

function kpiSub(pending: number, errored: number): string | undefined {
  const parts: string[] = [];
  if (pending > 0) parts.push(`${pending} pending`);
  if (errored > 0) parts.push(`${errored} error`);
  return parts.length ? parts.join(" · ") : undefined;
}

function summaryLine(connectors: Connector[] | null, c: Counts): string {
  if (connectors === null) return "Loading your calendars…";
  if (connectors.length === 0)
    return "No calendars yet — head to Connectors to request your first Gmail.";
  const bits: string[] = [];
  const totalActive = c.googleActive + c.localActive;
  if (totalActive > 0) bits.push(`${totalActive} active`);
  if (c.googlePending > 0) bits.push(`${c.googlePending} pending approval`);
  if (c.googleApproved > 0) bits.push(`${c.googleApproved} ready to connect`);
  if (c.googleRejected > 0) bits.push(`${c.googleRejected} rejected`);
  if (bits.length === 0) bits.push(`${connectors.length} total`);
  return bits.join(" · ") + ".";
}

function statusBadge(status: Connector["status"]): { label: string; cls: string; tone: "ok" | "error" | "warn" | "muted" | "info" } {
  switch (status) {
    case "active":
      return { label: "Active", cls: "bg-primary-container/15 text-primary-fixed-dim", tone: "ok" };
    case "approved":
      return { label: "Ready", cls: "bg-primary-container/15 text-primary-fixed-dim", tone: "ok" };
    case "awaiting_approval":
      return { label: "Pending", cls: "bg-tertiary-container/15 text-tertiary-container", tone: "warn" };
    case "error":
      return { label: "Error", cls: "bg-error-container/20 text-error", tone: "error" };
    case "paused":
      return { label: "Paused", cls: "bg-surface-variant text-on-surface-variant", tone: "muted" };
    case "rejected":
      return { label: "Rejected", cls: "bg-error-container/20 text-error", tone: "error" };
  }
}

// ---------- panels ----------

function ConnectorsPanel({
  connectors,
  className,
}: {
  connectors: Connector[] | null;
  className?: string;
}) {
  return (
    <GlassCard className={`p-lg ${className ?? ""}`}>
      <div className="flex items-center justify-between mb-md">
        <h2 className="text-headline-md text-on-surface">My connectors</h2>
        <Link to="/connectors" className="text-label-xs font-mono text-primary-fixed-dim uppercase tracking-widest">
          All
        </Link>
      </div>
      {connectors === null ? (
        <p className="text-body-sm text-on-surface-variant">Loading…</p>
      ) : connectors.length === 0 ? (
        <Link
          to="/connectors"
          className="block p-md border border-dashed border-primary-fixed-dim/30 rounded-lg hover:border-primary-fixed-dim/60 hover:bg-primary-container/5 transition-all text-center"
        >
          <Icon name="add_circle" className="text-primary-fixed-dim text-3xl" />
          <p className="text-body-sm text-on-surface mt-2">Request your first Gmail</p>
        </Link>
      ) : (
        <div className="space-y-2">
          {connectors.slice(0, 6).map((c) => {
            const badge = statusBadge(c.status);
            return (
              <Link
                key={c.id}
                to="/connectors"
                className="p-sm bg-surface-container-low rounded border border-outline-variant flex items-center justify-between gap-sm hover:border-primary-fixed-dim/60 transition-colors"
              >
                <div className="flex items-center gap-sm min-w-0">
                  <StatusPip tone={badge.tone} pulse={c.status === "active"} />
                  <div className="min-w-0">
                    <p className="text-body-sm text-on-surface truncate font-mono">
                      {c.email ?? c.name ?? c.id}
                    </p>
                    <p className="text-label-xs font-mono text-on-surface-variant uppercase">{c.type}</p>
                  </div>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-mono uppercase ${badge.cls}`}>
                  {badge.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}

function GettingStarted({
  connectors,
  className,
}: {
  connectors: Connector[] | null;
  className?: string;
}) {
  const counts = countByStatus(connectors);
  const hasGmail = (counts.googleActive + counts.googleApproved + counts.googlePending) > 0;
  const hasActiveGmail = counts.googleActive > 0;

  return (
    <GlassCard className={`p-lg ${className ?? ""}`}>
      <h2 className="text-headline-md text-on-surface mb-md">Getting started</h2>
      <ol className="space-y-md">
        <Step
          n={1}
          done={hasGmail}
          inProgress={!hasGmail}
          title="Request a Gmail connection"
          body={
            hasGmail
              ? "You've requested at least one Gmail."
              : "Go to Connectors → Request Gmail. Type the address you want to sync."
          }
          cta={!hasGmail ? { to: "/connectors", label: "Open Connectors" } : undefined}
        />
        <Step
          n={2}
          done={hasActiveGmail || counts.googleApproved > 0}
          inProgress={counts.googlePending > 0}
          title="Wait for admin approval"
          body={
            counts.googlePending > 0
              ? `Your request is pending. Email appsdo.ind@gmail.com if you'd like to nudge the admin.`
              : counts.googleApproved > 0 || hasActiveGmail
                ? "Approved by admin."
                : "After you request, the admin whitelists your email and approves it here."
          }
        />
        <Step
          n={3}
          done={hasActiveGmail}
          inProgress={counts.googleApproved > 0 && !hasActiveGmail}
          title="Finish via Google sign-in"
          body={
            hasActiveGmail
              ? "Connected and syncing."
              : counts.googleApproved > 0
                ? "Approved! Click Connect now on Connectors to finish via Google."
                : "Once approved, you'll click Connect now and sign in with Google."
          }
          cta={
            counts.googleApproved > 0 && !hasActiveGmail
              ? { to: "/connectors", label: "Connect now" }
              : undefined
          }
        />
        <Step
          n={4}
          done={counts.localActive > 0}
          inProgress={false}
          title="Install the desktop app on each PC"
          body="Coming soon. Once installed on your laptop + office PC, your local Outlook calendars sync to the hub automatically."
        />
      </ol>
    </GlassCard>
  );
}

function Step({
  n,
  done,
  inProgress,
  title,
  body,
  cta,
}: {
  n: number;
  done: boolean;
  inProgress: boolean;
  title: string;
  body: string;
  cta?: { to: string; label: string };
}) {
  // Explicit class names (not template-built) so Tailwind's JIT picks them up.
  const styles = done
    ? {
        circle: "bg-primary-container/15 border-primary-fixed-dim/30",
        text: "text-primary-fixed-dim",
        icon: "check_circle",
      }
    : inProgress
      ? {
          circle: "bg-tertiary-container/15 border-tertiary-container/30",
          text: "text-tertiary-container",
          icon: "more_horiz",
        }
      : {
          circle: "bg-surface-container-low border-outline-variant",
          text: "text-on-surface-variant",
          icon: "radio_button_unchecked",
        };

  return (
    <li className="flex gap-md">
      <div className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center text-label-xs font-mono ${styles.circle} ${styles.text}`}>
        {done || inProgress ? <Icon name={styles.icon} className={styles.text} /> : n}
      </div>
      <div className="flex-1">
        <p className="text-body-base text-on-surface font-semibold">{title}</p>
        <p className="text-body-sm text-on-surface-variant mt-0.5">{body}</p>
        {cta && (
          <Link
            to={cta.to}
            className="inline-flex items-center gap-1 text-label-xs font-mono uppercase tracking-widest text-primary-fixed-dim hover:underline mt-2"
          >
            {cta.label} <Icon name="arrow_forward" className="text-[14px]" />
          </Link>
        )}
      </div>
    </li>
  );
}

function Kpi({
  icon,
  label,
  value,
  sub,
  accent,
  cta,
  hint,
}: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  accent: "primary" | "secondary" | "tertiary" | "error";
  cta?: { to: string; label: string };
  hint?: string;
}) {
  const tone =
    accent === "primary"
      ? "text-primary-fixed-dim"
      : accent === "secondary"
        ? "text-secondary"
        : accent === "tertiary"
          ? "text-tertiary-fixed-dim"
          : "text-error";
  return (
    <GlassCard accent={accent} className="col-span-12 sm:col-span-6 xl:col-span-3 p-md">
      <div className="flex justify-between items-start mb-sm">
        <span className="text-on-surface-variant text-label-xs font-mono uppercase tracking-widest">{label}</span>
        <Icon name={icon} className={tone} />
      </div>
      <h3 className={`text-display-xl ${tone}`}>{value}</h3>
      {sub && (
        <p className="text-label-xs font-mono uppercase tracking-widest text-tertiary-container mt-1">{sub}</p>
      )}
      {hint && <p className="text-body-sm text-on-surface-variant mt-1">{hint}</p>}
      {cta && (
        <Link
          to={cta.to}
          className="inline-flex items-center gap-1 text-label-xs font-mono uppercase tracking-widest text-primary-fixed-dim hover:underline mt-2"
        >
          {cta.label} <Icon name="arrow_forward" className="text-[14px]" />
        </Link>
      )}
    </GlassCard>
  );
}

function HubLink({ health, error }: { health: HealthResponse | null; error: boolean }) {
  const tone = error ? "error" : health?.status === "ok" ? "ok" : "warn";
  return (
    <div className="flex items-center gap-sm bg-surface-container-low border border-outline-variant rounded-lg px-md py-2">
      <StatusPip tone={tone} pulse={!error} />
      <div className="text-body-sm">
        <span className="font-mono text-label-xs uppercase tracking-widest text-on-surface-variant mr-2">Hub</span>
        {error ? (
          <span className="text-error">unreachable</span>
        ) : health ? (
          <span className="text-on-surface font-mono">
            {health.status} · db {health.db} · redis {health.redis} · v{health.version}
          </span>
        ) : (
          <span className="text-on-surface-variant">probing…</span>
        )}
      </div>
    </div>
  );
}

function PhaseRow({
  done,
  partial,
  inProgress,
  pending,
  label,
  detail,
}: {
  done?: boolean;
  partial?: boolean;
  inProgress?: boolean;
  pending?: boolean;
  label: string;
  detail: string;
}) {
  const { icon, tone } = done
    ? { icon: "check_circle", tone: "text-primary-fixed-dim" }
    : partial
      ? { icon: "check_circle_outline", tone: "text-tertiary-container" }
      : inProgress
        ? { icon: "sync", tone: "text-secondary animate-spin" }
        : { icon: "radio_button_unchecked", tone: "text-on-surface-variant" };
  void pending;
  return (
    <div className="flex items-start gap-sm py-1">
      <Icon name={icon} className={tone} />
      <div className="flex-1">
        <p className="text-body-sm text-on-surface font-medium">{label}</p>
        <p className="text-body-sm text-on-surface-variant">{detail}</p>
      </div>
    </div>
  );
}
